/**
 * SegmentClassifier - Finite State Machine that classifies processed GPS points
 * into segments (downhill runs, lift rides, and pauses/stops).
 *
 * @typedef {import('../types/index.js').GpsPoint} GpsPoint
 * @typedef {import('../types/index.js').Segment} Segment
 */

import { v4 as uuidv4 } from 'uuid';
import { calculateDistance } from './GpsProcessor.js';

// ─── FSM States ──────────────────────────────────────────────────────────────
/** @enum {string} */
export const ActivityState = {
  STOPPED: 'STOPPED',
  ON_LIFT: 'ON_LIFT',
  SKIING: 'SKIING',
  TRAVERSING: 'TRAVERSING',
};

// ─── Thresholds ──────────────────────────────────────────────────────────────

const THRESHOLDS = {
  // Speed thresholds (m/s)
  STOP_SPEED: 1.0,           // below this for N seconds => STOPPED
  LIFT_MIN_SPEED: 1.5,       // moving, but not fast
  LIFT_MAX_SPEED: 8.0,       // lifts rarely exceed ~8 m/s (~29 km/h)
  SKIING_MIN_SPEED: 3.0,     // minimum speed to start classifying as skiing
  TRAVERSE_MAX_SPEED: 5.0,   // traversing is slow lateral movement

  // Duration thresholds (ms)
  SKIING_TO_STOP_DURATION: 45_000,    // must be slow for 45s to register as stopped
  LIFT_TO_STOP_DURATION: 20_000,      // must be stationary 20s after lift
  MIN_LIFT_ASCENT_DURATION: 30_000,   // need 30s of ascending to confirm lift
  MIN_SEGMENT_DURATION: 10_000,       // discard segments shorter than 10s

  // Altitude thresholds (meters)
  MIN_ALTITUDE_CHANGE: 5,  // noise floor for altitude change in a window

  // Heading variance
  LOW_HEADING_VARIANCE: 400,  // squared-degree threshold; lifts have low variance
};

/**
 * Compute circular variance of headings (in degrees).
 * Uses the mean resultant length approach.
 * Returns a value in [0, ~infinity) where 0 = constant heading.
 * @param {number[]} headings
 * @returns {number}
 */
function headingVariance(headings) {
  if (headings.length < 2) return 0;
  let sinSum = 0;
  let cosSum = 0;
  for (const h of headings) {
    const rad = (h * Math.PI) / 180;
    sinSum += Math.sin(rad);
    cosSum += Math.cos(rad);
  }
  const n = headings.length;
  const R = Math.sqrt((sinSum / n) ** 2 + (cosSum / n) ** 2); // mean resultant length [0,1]
  // Variance approximation: 1 - R  (ranges from 0 = no spread to 1 = uniform spread)
  // We scale to degrees-squared-like value for easier threshold comparison.
  return (1 - R) * 180 * 180;
}

/**
 * Compute net altitude change over a window of points.
 * Positive = ascending, negative = descending.
 * @param {GpsPoint[]} window
 * @returns {number}
 */
function netAltitudeChange(window) {
  if (window.length < 2) return 0;
  return window[window.length - 1].altitude - window[0].altitude;
}

/**
 * Compute average speed over a window of points (m/s).
 * @param {GpsPoint[]} window
 * @returns {number}
 */
function avgSpeed(window) {
  if (window.length === 0) return 0;
  return window.reduce((sum, p) => sum + (p.speed || 0), 0) / window.length;
}

/**
 * Build a Segment object from a slice of classified points.
 * @param {GpsPoint[]} points
 * @param {string} fsmState - one of ActivityState values
 * @returns {Segment}
 */
function buildSegment(points, fsmState) {
  /** @type {import('../types/index.js').SegmentType} */
  let type;
  switch (fsmState) {
    case ActivityState.SKIING:
    case ActivityState.TRAVERSING:
      type = 'downhill';
      break;
    case ActivityState.ON_LIFT:
      type = 'lift';
      break;
    default:
      type = 'pause';
  }

  let distance = 0;
  let elevationGain = 0;
  let elevationLoss = 0;
  let maxSpeed = 0;
  let speedSum = 0;

  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    if (p.speed > maxSpeed) maxSpeed = p.speed;
    speedSum += p.speed || 0;

    if (i > 0) {
      distance += calculateDistance(points[i - 1], p);
      const altDiff = p.altitude - points[i - 1].altitude;
      if (altDiff > 0) elevationGain += altDiff;
      else elevationLoss += Math.abs(altDiff);
    }
  }

  const startTime = points[0].timestamp;
  const endTime = points[points.length - 1].timestamp;

  return {
    id: uuidv4(),
    type,
    points: [...points],
    startTime,
    endTime,
    distance,
    elevationGain,
    elevationLoss,
    maxSpeed,
    avgSpeed: points.length > 0 ? speedSum / points.length : 0,
    duration: endTime - startTime,
  };
}

/**
 * Classify a processed track into segments using a finite state machine
 * with hysteresis to prevent rapid state switching.
 *
 * @param {GpsPoint[]} processedPoints - Points already filtered/smoothed/enriched
 * @returns {Segment[]}
 */
export function classifyTrack(processedPoints) {
  if (!processedPoints || processedPoints.length < 2) return [];

  const segments = [];

  let state = ActivityState.STOPPED;
  let segmentStartIdx = 0;

  // Hysteresis counters: how long (ms) the conditions for a transition have held
  let transitionTimer = 0;
  let lastTimestamp = processedPoints[0].timestamp;

  // Rolling window for contextual analysis
  const WINDOW_SIZE = 8;

  for (let i = 1; i < processedPoints.length; i++) {
    const point = processedPoints[i];
    const dt = point.timestamp - lastTimestamp;
    lastTimestamp = point.timestamp;

    // Build a rolling window ending at i
    const winStart = Math.max(0, i - WINDOW_SIZE + 1);
    const window = processedPoints.slice(winStart, i + 1);

    const currentSpeed = point.speed || 0;
    const windowAvgSpeed = avgSpeed(window);
    const altChange = netAltitudeChange(window);
    const headings = window.map((p) => p.heading).filter((h) => h != null);
    const hVariance = headingVariance(headings);

    let nextState = state;

    switch (state) {
      // ─── STOPPED ────────────────────────────────────────────────────────
      case ActivityState.STOPPED: {
        if (
          windowAvgSpeed > THRESHOLDS.LIFT_MIN_SPEED &&
          windowAvgSpeed < THRESHOLDS.LIFT_MAX_SPEED &&
          altChange > THRESHOLDS.MIN_ALTITUDE_CHANGE
        ) {
          // Potential lift ride: ascending at moderate speed
          transitionTimer += dt;
          if (transitionTimer >= THRESHOLDS.MIN_LIFT_ASCENT_DURATION) {
            nextState = ActivityState.ON_LIFT;
            transitionTimer = 0;
          }
        } else if (
          currentSpeed > THRESHOLDS.SKIING_MIN_SPEED &&
          altChange < -THRESHOLDS.MIN_ALTITUDE_CHANGE
        ) {
          // Fast and descending => skiing
          nextState = ActivityState.SKIING;
          transitionTimer = 0;
        } else if (
          windowAvgSpeed > THRESHOLDS.LIFT_MIN_SPEED &&
          Math.abs(altChange) <= THRESHOLDS.MIN_ALTITUDE_CHANGE
        ) {
          // Moving on relatively flat ground
          nextState = ActivityState.TRAVERSING;
          transitionTimer = 0;
        } else {
          // Stay stopped, reset hysteresis timer if conditions broke
          if (windowAvgSpeed <= THRESHOLDS.LIFT_MIN_SPEED || altChange <= THRESHOLDS.MIN_ALTITUDE_CHANGE) {
            transitionTimer = 0;
          }
        }
        break;
      }

      // ─── ON_LIFT ────────────────────────────────────────────────────────
      case ActivityState.ON_LIFT: {
        if (currentSpeed < THRESHOLDS.STOP_SPEED * 0.5) {
          // Possibly reached the top
          transitionTimer += dt;
          if (transitionTimer >= THRESHOLDS.LIFT_TO_STOP_DURATION) {
            nextState = ActivityState.STOPPED;
            transitionTimer = 0;
          }
        } else {
          transitionTimer = 0;
          // If suddenly fast and descending, the GPS may have jumped or
          // the rider started skiing off the lift.
          if (currentSpeed > THRESHOLDS.SKIING_MIN_SPEED && altChange < -THRESHOLDS.MIN_ALTITUDE_CHANGE) {
            nextState = ActivityState.SKIING;
            transitionTimer = 0;
          }
        }
        break;
      }

      // ─── SKIING ─────────────────────────────────────────────────────────
      case ActivityState.SKIING: {
        if (currentSpeed < THRESHOLDS.STOP_SPEED) {
          transitionTimer += dt;
          if (transitionTimer >= THRESHOLDS.SKIING_TO_STOP_DURATION) {
            nextState = ActivityState.STOPPED;
            transitionTimer = 0;
          }
        } else {
          transitionTimer = 0;
          // If ascending at moderate speed with low heading variance => lift
          if (
            windowAvgSpeed > THRESHOLDS.LIFT_MIN_SPEED &&
            windowAvgSpeed < THRESHOLDS.LIFT_MAX_SPEED &&
            altChange > THRESHOLDS.MIN_ALTITUDE_CHANGE &&
            hVariance < THRESHOLDS.LOW_HEADING_VARIANCE
          ) {
            nextState = ActivityState.ON_LIFT;
            transitionTimer = 0;
          }
        }
        break;
      }

      // ─── TRAVERSING ────────────────────────────────────────────────────
      case ActivityState.TRAVERSING: {
        if (currentSpeed < THRESHOLDS.STOP_SPEED) {
          transitionTimer += dt;
          if (transitionTimer >= THRESHOLDS.SKIING_TO_STOP_DURATION) {
            nextState = ActivityState.STOPPED;
            transitionTimer = 0;
          }
        } else {
          transitionTimer = 0;
          if (currentSpeed > THRESHOLDS.SKIING_MIN_SPEED && altChange < -THRESHOLDS.MIN_ALTITUDE_CHANGE) {
            nextState = ActivityState.SKIING;
          } else if (
            windowAvgSpeed > THRESHOLDS.LIFT_MIN_SPEED &&
            windowAvgSpeed < THRESHOLDS.LIFT_MAX_SPEED &&
            altChange > THRESHOLDS.MIN_ALTITUDE_CHANGE
          ) {
            transitionTimer += dt;
            if (transitionTimer >= THRESHOLDS.MIN_LIFT_ASCENT_DURATION) {
              nextState = ActivityState.ON_LIFT;
              transitionTimer = 0;
            }
          }
        }
        break;
      }
    }

    // ─── State transition: emit segment ─────────────────────────────────
    if (nextState !== state) {
      const segPoints = processedPoints.slice(segmentStartIdx, i);
      if (segPoints.length >= 2) {
        const seg = buildSegment(segPoints, state);
        // Only keep segments longer than the minimum duration
        if (seg.duration >= THRESHOLDS.MIN_SEGMENT_DURATION) {
          segments.push(seg);
        }
      }
      segmentStartIdx = i;
      state = nextState;
    }
  }

  // ─── Final segment ──────────────────────────────────────────────────
  const remaining = processedPoints.slice(segmentStartIdx);
  if (remaining.length >= 2) {
    const seg = buildSegment(remaining, state);
    if (seg.duration >= THRESHOLDS.MIN_SEGMENT_DURATION) {
      segments.push(seg);
    }
  }

  return segments;
}

/**
 * Determine the current activity from the most recent points.
 * Useful for live display during recording.
 *
 * @param {GpsPoint[]} recentPoints - The last ~10 points
 * @returns {{ type: string, speed: number, altitude: number }}
 */
export function classifyCurrentActivity(recentPoints) {
  if (!recentPoints || recentPoints.length < 2) {
    return { type: 'stopped', speed: 0, altitude: recentPoints?.[0]?.altitude || 0 };
  }

  const latest = recentPoints[recentPoints.length - 1];
  const windowAvg = avgSpeed(recentPoints);
  const altChange = netAltitudeChange(recentPoints);

  if (windowAvg < THRESHOLDS.STOP_SPEED) {
    return { type: 'stopped', speed: latest.speed || 0, altitude: latest.altitude };
  }
  if (
    windowAvg >= THRESHOLDS.LIFT_MIN_SPEED &&
    windowAvg < THRESHOLDS.LIFT_MAX_SPEED &&
    altChange > THRESHOLDS.MIN_ALTITUDE_CHANGE
  ) {
    return { type: 'lift', speed: latest.speed || 0, altitude: latest.altitude };
  }
  if (windowAvg >= THRESHOLDS.SKIING_MIN_SPEED && altChange < -THRESHOLDS.MIN_ALTITUDE_CHANGE) {
    return { type: 'skiing', speed: latest.speed || 0, altitude: latest.altitude };
  }
  return { type: 'traversing', speed: latest.speed || 0, altitude: latest.altitude };
}
